import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import classNames from 'classnames'
import styles from './styles.module.scss'
import CustomField from '../../../form/CustomField'
import IconCalendar from '/public/icons/icon-calendar.svg'
import { useResponsive } from '../../../../../providers/ResponsiveContext'
import { useSearchContext } from '../../../../../providers/SearchContext'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

// Lazily load Calendar component
const Calendar = dynamic(() => import('../../../form/Calendar'), {
  ssr: false,
  loading: () => <div>Loading calendar...</div>
})

const Dates = memo(
  ({
    form: { setFieldValue, values },
    shadowRef,
    setCalendarActive,
    variant = [],
    custom,
    icon = false,
    translations
  }) => {
    const { search, reset, initialValues, setOffsetable } = useSearchContext()
    const router = useRouter()
    const [showCalendar, setShowCalendar] = useState(false)
    const wrapperRef = useRef(null)
    const { isMobile, isTablet } = useResponsive()
    const [startDate, setStartDate] = useState(search?.startDate)
    const [endDate, setEndDate] = useState(search?.endDate)
    const [loaded, setLoaded] = useState(false)

    // Memoize formatted date strings to avoid recalculation
    const formattedDates = useCallback(() => {
      if (!startDate || !endDate) return translations.dates[router.locale]

      const startDateString = startDate.format('MMM D')
      const endDateString = endDate.format('MMM D')
      return `${startDateString} - ${endDateString}`
    }, [startDate, endDate, translations, router.locale])

    // Handle click outside
    const handleClickOutside = useCallback((event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowCalendar(false)
      }
    }, [])

    // Set up click outside handler
    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [handleClickOutside])

    // Sync with search context
    useEffect(() => {
      setStartDate(search.startDate)
      setEndDate(search.endDate)
    }, [search.startDate, search.endDate])

    // Update form values when dates change
    useEffect(() => {
      setFieldValue('startDate', startDate)
      setFieldValue('endDate', endDate)
    }, [startDate, endDate, setFieldValue])

    // Handle shadow overlay
    useEffect(() => {
      if (shadowRef) {
        if (showCalendar) {
          shadowRef.current?.classList.add('isVisible')
        } else {
          shadowRef.current?.classList.remove('isVisible')
        }
      }
    }, [shadowRef, showCalendar])

    // Handle reset from search context
    useEffect(() => {
      if (reset) {
        setStartDate(initialValues.startDate)
        setEndDate(initialValues.endDate)
      }
    }, [reset, initialValues])

    // Update parent component's active state
    useEffect(() => {
      if (setCalendarActive) {
        setCalendarActive((search.startDate && search.endDate) || showCalendar)
      }
    }, [
      showCalendar,
      search.startDate,
      search.endDate,
      setCalendarActive,
      values
    ])

    // Set loaded state after mount
    useEffect(() => setLoaded(true), [])

    // Handle toggle calendar
    const toggleCalendar = useCallback(() => {
      setShowCalendar((prev) => !prev)
    }, [])

    // Width calculation
    const fieldWidth =
      startDate && endDate ? (isTablet ? 9.5 : isMobile ? 11 : 13.5) : 9.56

    // Date range display
    const dateRangeString = formattedDates()

    // Track if calendar content should be in DOM with a separate state
    const [calendarMounted, setCalendarMounted] = useState(false)

    // Use effect to handle mounting/unmounting with proper timing for transitions
    useEffect(() => {
      if (showCalendar) {
        // When opening, mount content immediately
        setCalendarMounted(true)
      } else if (!showCalendar && calendarMounted) {
        // When closing, wait for transition to finish before unmounting
        const transitionDuration = 300 // Match this with your CSS transition duration
        const timer = setTimeout(() => {
          setCalendarMounted(false)
        }, transitionDuration)

        return () => clearTimeout(timer)
      }
    }, [showCalendar, calendarMounted])

    return (
      <div ref={wrapperRef} className={withModifiers('dates', variant, styles)}>
        <CustomField
          variant={[...variant, isMobile && 'full-width']}
          aria-label="Select Dates"
          angle
          readOnly
          isActive={(startDate && endDate) || showCalendar}
          name="calendar"
          width={fieldWidth}
          prependIcon={icon && <IconCalendar width={26} height={26} />}
          value={dateRangeString}
          onClick={toggleCalendar}
        />

        <div
          className={classNames(
            styles.dates__wrapper,
            showCalendar && styles.isActive,
            custom && styles.custom
          )}
        >
          {loaded && calendarMounted && (
            <Calendar
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              startDate={startDate}
              setOffsetDays={setOffsetable}
              endDate={endDate}
              translations={translations}
              withOffset
            />
          )}
        </div>
      </div>
    )
  }
)

// Helper function for class modifiers - moving from the import to avoid dependencies
const withModifiers = (block, modifiers = [], styles) => {
  if (!modifiers || modifiers.length === 0 || !styles) return styles[block]

  const classes = [styles[block]]
  modifiers.forEach((modifier) => {
    if (modifier) {
      classes.push(styles[`${block}--${modifier}`])
    }
  })

  return classes.join(' ')
}

// Display name for debugging
Dates.displayName = 'Dates'

export default Dates
